import { Body, Box, Heading, Image } from '@hover/blueprint';
import React, { useContext } from 'react';

import {
  FrameFilterItems,
  GridFilterItems,
  WindowConfigImgs,
} from 'src/modules/designStudio/constants';
import type {
  FrameStyles,
  GridStyles,
} from 'src/modules/designStudio/constants/hardCodedData/windowParametricData';
import { usePartner } from 'src/modules/designStudio/hooks';
import {
  Partner,
  productCatalogContext,
} from 'src/modules/designStudio/providers';
import type { LegacyDesignConfig } from 'src/modules/designStudio/types';

const TextureProperty = ({
  label,
  value,
  testId,
}: {
  label: string;
  value: string;
  testId?: string;
}) => (
  <Box testId={testId || ''}>
    <Body
      color="neutral500"
      flex="1"
      fontWeight={400}
      marginBottom={100}
      marginTop={0}
      size={400}
    >
      {label}
    </Body>
    <Body flex="1" marginBottom={100} marginTop={0} size={400}>
      {value}
    </Body>
  </Box>
);

const toTitleCase = (str: string) => str[0].toUpperCase() + str.slice(1);

const DesignChangeCard = ({
  color,
  title,
  textureId,
  windowType,
  gridStyle,
  isTrim,
}: {
  color: string | null;
  title: string;
  textureId?: number;
  windowType?: FrameStyles;
  gridStyle?: GridStyles;
  isTrim?: boolean;
}) => {
  const partner = usePartner();
  const { filteredColors, menuItems } = useContext(productCatalogContext);
  const isJH = partner === Partner.JAMES_HARDIE;

  const { textures } = useContext(productCatalogContext);
  const texture = menuItems.find(item => item.textureId === textureId);
  const windowImg =
    gridStyle && windowType && WindowConfigImgs[windowType][gridStyle];
  const textureImg = textures?.find(item => item.id === textureId)?.texture
    ?.thumbnail.url;
  const colorGroup =
    texture?.colorGroup ||
    texture?.name ||
    filteredColors.WALLS.find(
      item => item.color.toLowerCase() === color?.toLowerCase(),
    )?.name ||
    filteredColors.WINDOWS.find(
      item => item.color.toLowerCase() === color?.toLowerCase(),
    )?.name;
  const isTextureWithColor = texture?.hasThumbnail;
  const windowTypeLabel = windowType
    ? FrameFilterItems.find(item => item.id === windowType)?.label
    : '';
  const gridStyleLabel = gridStyle
    ? GridFilterItems.find(item => item.id === gridStyle)?.label
    : '';

  return (
    <Box
      borderBottom="1px solid #e6e6e6"
      display="grid"
      marginBottom={400}
      paddingBottom={400}
    >
      <Heading paddingBottom={300} size={200}>
        {title}
      </Heading>
      <Box columnGap="500" display="flex">
        <Box height="88px" position="relative" width="88px">
          {isTrim && (
            <Box
              // @ts-expect-error - we have custom colors in DS
              backgroundColor={color}
              borderRadius={400}
              testId="trim-img"
              width="88px"
            />
          )}
          {!isTrim && (
            <>
              <Image
                alt={`textureId: ${textureId || ''}`}
                borderColor="neutral.600"
                borderRadius={400}
                height="88px"
                src={windowImg || textureImg}
                width="88px"
              />
              {!isTextureWithColor && (
                <Box
                  // @ts-expect-error - we have custom colors in DS
                  backgroundColor={color}
                  border="2px solid #ffffff"
                  borderRadius="5px"
                  bottom="9px"
                  boxShadow="0px 0px 4px rgba(0, 0, 0, 0.25)"
                  height="24px"
                  position="absolute"
                  right="9px"
                  testId="color-box"
                  width="24px"
                />
              )}
            </>
          )}
        </Box>
        <Box display="block" width="100%">
          {texture?.type && (
            <TextureProperty
              label="Siding Type"
              value={toTitleCase(texture.type)}
            />
          )}
          {windowTypeLabel && (
            <TextureProperty
              label="Window Type"
              testId="window-type"
              value={windowTypeLabel}
            />
          )}
          {gridStyleLabel && (
            <TextureProperty
              label="Grid Style"
              testId="grid-style"
              value={gridStyleLabel}
            />
          )}
          {texture?.style && (
            <TextureProperty
              label="Style"
              testId="style"
              value={toTitleCase(texture.style)}
            />
          )}
          {texture?.shape && (
            <TextureProperty label="Shape" value={toTitleCase(texture.shape)} />
          )}
          {texture?.size && (
            <TextureProperty label="Size" testId="size" value={texture.size} />
          )}
          {colorGroup && (
            <TextureProperty
              label={isJH ? 'Color' : 'Color Group'}
              testId="color"
              value={colorGroup}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

const DesignChanges = ({
  designConfig,
}: {
  designConfig: LegacyDesignConfig;
}) => (
  <Box display="grid" testId="design-changes">
    {designConfig.WALLS.textureId && (
      <DesignChangeCard
        color={designConfig.WALLS.color}
        textureId={designConfig.WALLS.textureId}
        title="Wall"
      />
    )}

    {designConfig.ROOFS.textureId && (
      <DesignChangeCard
        color={designConfig.ROOFS.color}
        textureId={designConfig.ROOFS.textureId}
        title="Roof"
      />
    )}
    {designConfig.WINDOWS.gridType && (
      <DesignChangeCard
        color={designConfig.WINDOWS.color}
        gridStyle={designConfig.WINDOWS.gridType as GridStyles}
        title="Windows"
        windowType={designConfig.WINDOWS.frameType as FrameStyles}
      />
    )}
    {designConfig.TRIMS.color && (
      <DesignChangeCard color={designConfig.TRIMS.color} isTrim title="Trim" />
    )}
  </Box>
);

export { DesignChanges };
